import React from 'react';
import {
  cssColorValue,
  screenSmMin,
  semanticColors,
  spacer32,
  spacer40,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';
import { DocumentLink, SanityArticleData } from 'lib/sanity/types';
import IconChevron from 'components/IconChevron';
import Ellipsis from 'components/Ellipsis';
import { HomeLink, ParentCategoryLink } from 'components/Links';
import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { UbiParentCategoryPage } from 'utils/ubi/pages/useUbiParentCategory';

const StyledList = styled.ul`
  display: flex;
  flex-direction: row;
  margin-block-end: ${spacer32};
  padding: 0;
  color: ${cssColorValue(semanticColors.textSubdued)};
  width: 100%;
  list-style: none;
  margin-top: 0;

  @media (min-width: ${screenSmMin}) {
    margin-block-end: ${spacer40};
  }
`;

const StyledListItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  white-space: nowrap;
`;

const StyledListItemWithEllipsis = styled(StyledListItem)`
  overflow: hidden;
`;

const StyledListItemHidden = styled(StyledListItem)`
  display: none;
`;

const StyledLinkWithEllipsis = styled(ParentCategoryLink)`
  overflow: hidden;
`;

const StyledSeparator = styled.div`
  margin: 0 ${spacer8};
  display: inline-flex;
  align-items: center;
`;

interface BreadcrumbsProps {
  parentCategory: DocumentLink;
  article?: SanityArticleData;
  ubi: UbiArticlePage | UbiParentCategoryPage;
}

const Breadcrumbs = ({ parentCategory, article, ubi }: BreadcrumbsProps) => {
  const { t } = useTranslation();

  // Since Sanity document relationship is handled through the english master,
  // if the parentcategory is not translated, but the article is published
  // the title can be empty, and then we should not render the breadcrumb.
  const { title: articleTitle } = article ?? {};

  const { title: categoryTitle, slug: categorySlug } = parentCategory ?? {};

  return (
    <nav aria-label={t('breadcrumbs')}>
      <StyledList>
        <StyledListItem>
          <HomeLink ubiFactory={ubi.logBreadcrumbHomeLink()} standalone>
            <Type variant="ballad" weight="bold" data-testid="breadcrumbHome">
              {t('breadcrumb_home')}
            </Type>
          </HomeLink>
          <StyledSeparator role="separator">
            <IconChevron size={16} />
          </StyledSeparator>
        </StyledListItem>
        {categorySlug &&
          categoryTitle &&
          (articleTitle ? (
            <>
              <StyledListItemWithEllipsis>
                <StyledLinkWithEllipsis
                  parentCategorySlug={categorySlug}
                  data-testid="breadcrumbCategory"
                  standalone
                  ubiFactory={(ubi as UbiArticlePage).logBreadcrumbLink?.(categorySlug)}
                >
                  <Ellipsis variant="ballad" weight="bold">
                    {categoryTitle}
                  </Ellipsis>
                </StyledLinkWithEllipsis>
                <StyledSeparator role="separator">
                  <IconChevron size={16} />
                </StyledSeparator>
              </StyledListItemWithEllipsis>
              <StyledListItemHidden aria-hidden="true" aria-current="page" hidden>
                {articleTitle}
              </StyledListItemHidden>
            </>
          ) : (
            <StyledListItemHidden aria-hidden="true" aria-current="page" hidden>
              {categoryTitle}
            </StyledListItemHidden>
          ))}
      </StyledList>
    </nav>
  );
};

export default Breadcrumbs;
